import React, {createContext, useState, ReactNode, useContext, useEffect, useCallback} from 'react';
import { getAuth, signOut } from 'firebase/auth';
import {Chat, Message} from "../lib/types";
import {useUserContext} from "./UserContext";
import {getRef} from "../lib/database";
import {get, onValue, orderByChild, query, startAt} from "firebase/database";
import {updateFaviconWithCounter} from "../lib/utils/favicon";
import {playNotificationSound} from "../lib/utils/notification";
import useTabVisibility from "../lib/hooks/PageVisibility";
import {getItem, mergeChats, setItem} from "../lib/localStorage";
 interface InboxContextType {
  inbox: Chat[];
  unreadCount: number;
  inboxVisible: boolean;
  setInboxVisible: (visible: boolean) => void;
  isUnread: (chat: Chat) => boolean;
   isChatInInbox: (chat: Chat) => boolean;
}

 export const InboxContext = createContext<InboxContextType | undefined>(undefined);

 interface UserProviderProps {
  children: ReactNode;
}

export const InboxProvider: React.FC<UserProviderProps> = ({ children }) => {
  const [inbox, setInboxes] = useState<Chat[]>(getItem('inbox') || []);
  const [unreadCount, setUnreadCountState] = useState(0);
  const { user, activity, activeWorkspace, activeChatId, unreadChats, fetchMySidebar } = useUserContext();
  const isTabActive = useTabVisibility();

  const [inboxLoading, setInboxLoading] = useState(true);
  const [inboxVisible, setInboxVisible] = useState(false);
  const [lastLoadedInboxTimestamp, setLastLoadedInboxTimestamp] = useState<number>(getItem('lastLoadedInboxTimestamp') || 0);
  const isChatInInbox = useCallback((chat: Chat): boolean => {
    return !!inbox.find(inboxChat => inboxChat.id === chat.id);
  }, [inbox]);
  const isUnread = useCallback((chat: Chat) => {
    if (!activity || !chat.id || Object.keys(activity.chats).length === 0 || !unreadChats) {
      return false;
    }
    if(chat?.lastMessage?.authorId === user?.uid) {
      return false;
    }
    return unreadChats.some(unreadChat => unreadChat.id === chat.id);
  }, [activity, unreadChats, user]);

  const [lastMessage, setLastMessage] = useState<Message|undefined>(undefined);

  const setUnreadCount = (count: number) => {
    if(count > 0) {
      document.title = `(${count}) Nima`;
    } else {
      document.title = `Nima`;
    }
    updateFaviconWithCounter(count);
    setUnreadCountState(count);
  }

  useEffect(() => {
    fetchMySidebar();
    if(!inboxLoading && !isTabActive && lastMessage?.authorId !== user?.uid) {
      playNotificationSound();
    }
  }, [lastMessage]);

  const mergeInboxLocalStorage = (inbox: Chat[]) => {
    const localInbox = getItem('inbox') || [];
    mergeChats(localInbox, inbox, (mergedInbox) => {
      if (mergedInbox.length < localInbox.length) {
        console.warn("Inbox has shrunk, not saving");
        console.trace("Inbox has shrunk, not saving");
        return localInbox;
      }
      setItem('inbox', mergedInbox);
      setInboxes(mergedInbox);
      let lastLoadedTimestamp = mergedInbox[0]?.timestamp || 0;
      const generatingTitle = mergedInbox.filter((chat) => chat.title.toLowerCase().includes("generating title"));
      if (generatingTitle.length > 0) {
        lastLoadedTimestamp = Math.min(...generatingTitle.map((chat) => chat.timestamp || 0)) - 1;
      }

      setItem('lastLoadedInboxTimestamp', lastLoadedTimestamp);
      setLastLoadedInboxTimestamp(lastLoadedTimestamp);
      return mergedInbox;
    });
  }

  useEffect(() => {
    let unsubscribe: any;
    if (user && activeWorkspace) {

      // const inboxRef = getRef('inbox/'+user.uid);
      // if(lastTimestamp) {
      //   inboxRef.orderByChild('lastMessage/timestamp').startAt(lastTimestamp);
      // }

      const inboxRef = getRef('inbox/'+user.uid);
      let inboxQuery = query(inboxRef, orderByChild('lastMessage/timestamp'));

      if (lastLoadedInboxTimestamp) {
        inboxQuery = query(inboxRef, orderByChild('lastMessage/timestamp'), startAt(lastLoadedInboxTimestamp+1));
      }
      console.info("Subscribed inbox");

      unsubscribe = onValue(inboxQuery, (snapshot) => {
        const data = snapshot.val();
        console.info("Loaded inbox: ",snapshot.size);
        console.log(data);
        if(data) {
          const inbox = Object.values(data).map((i: any) => {
            if(!i.groupPermissions) {
              i.groupPermissions = {};
            }
            if(!i.userPermissions) {
              i.userPermissions = {};
            }
            return i as Chat;
          }).filter(
            (chat: Chat) => chat.workspaceId === activeWorkspace?.id
          )
          inbox.sort((a, b) => (a.lastMessage && b.lastMessage) ? b.lastMessage.timestamp - a.lastMessage.timestamp : 0)
          mergeInboxLocalStorage(inbox);
          setInboxLoading(false);
        }
      });
    }
    return () => {
      if (unsubscribe) {
        console.info("Unsubscribed inbox");
        unsubscribe();
      }
    }
  }, [user, activeWorkspace, lastLoadedInboxTimestamp]);

  useEffect(() => {
    setInboxes(getItem('inbox') || []);
  }, []);
  useEffect(() => {
    setLastMessage(inbox[0]?.lastMessage);
    const unread = inbox.filter(isUnread);
    setUnreadCount(unread.length);
  }, [inbox, activity]);


  return (
     <InboxContext.Provider value={{isChatInInbox, inbox, unreadCount, inboxVisible, setInboxVisible, isUnread }}>
      {children}
    </InboxContext.Provider>
  );
};

export const useInboxContext = (): InboxContextType => {
  const context = useContext(InboxContext);
  if (!context) {
    throw new Error('useUserContext must be used within a UserProvider');
  }
  return context;
};


export const logout = () => {
  const auth = getAuth();
  signOut(auth);
}
